import { useAuth } from "@/auth/AuthProvider";
import { ROLE_LABELS, type Role } from "@/auth/roles";
import { BrewisMark } from "@/components/BrewisMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { type NavSection, type NavTab, sectionByPath, visibleSections } from "@/config/nav";
import { useRisksSummary } from "@/features/risks/hooks";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { ChevronsLeft, ChevronsRight, LogOut, Menu } from "lucide-react";
import { useState } from "react";

/* ───── sidebar sizing ──────────────────────────────────────────────── */

const SIDEBAR_EXPANDED = 220;
const SIDEBAR_COLLAPSED = 60;
const STORAGE_KEY = "brewis_sidebar";

function useSidebarState() {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== "collapsed";
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "expanded" : "collapsed");
      } catch {}
      return next;
    });
  };

  return { expanded, toggle, width: expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED };
}

/* ───── route ────────────────────────────────────────────────────────── */

export const Route = createFileRoute("/_auth")({
  beforeLoad: ({ context, location }) => {
    if (context.auth.status === "unauthenticated") {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: AuthLayout,
});

/* ───── layout ───────────────────────────────────────────────────────── */

function AuthLayout() {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  const isMobileNav = isMdDown || isCoarsePointer;
  const { user, role, status, signOutUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebar = useSidebarState();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const sections = visibleSections(role);
  const currentSection = sectionByPath(location.pathname) ?? sections[0];
  const showNav = status === "authenticated" && sections.length > 0;

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh", bgcolor: "background.default" }}>
      {/* ── sidebar ── */}
      {showNav && !isMobileNav && (
        <Sidebar
          sections={sections}
          expanded={sidebar.expanded}
          width={sidebar.width}
          onToggle={sidebar.toggle}
          user={user}
          role={role}
          mobile={false}
          onSignOut={async () => {
            await signOutUser();
            await navigate({ to: "/login" });
          }}
        />
      )}

      {showNav && isMobileNav && (
        <Drawer
          anchor="left"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: 280,
              pt: "var(--safe-top)",
              pb: "var(--safe-bottom)",
            },
          }}
        >
          <Sidebar
            sections={sections}
            expanded
            width={280}
            onToggle={() => setMobileNavOpen(false)}
            user={user}
            role={role}
            mobile
            onItemClick={() => setMobileNavOpen(false)}
            onSignOut={async () => {
              setMobileNavOpen(false);
              await signOutUser();
              await navigate({ to: "/login" });
            }}
          />
        </Drawer>
      )}

      {/* ── main content ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* top bar */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
            pt: { xs: "var(--safe-top)", md: 0 },
            height: { xs: "calc(56px + var(--safe-top))", md: 56 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {showNav && isMobileNav && (
              <IconButton onClick={() => setMobileNavOpen(true)} aria-label="Відкрити навігацію">
                <Menu size={20} />
              </IconButton>
            )}
            <Typography variant="h6" fontWeight={600} noWrap>
              {currentSection?.label ?? "Brewis"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ThemeToggle />
          </Stack>
        </Box>

        {/* sub-tabs if section has >1 tab */}
        {status === "authenticated" && currentSection && currentSection.tabs.length > 1 && (
          <SubTabsBar section={currentSection} />
        )}

        {/* page content */}
        <Box
          sx={{
            flex: 1,
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: { xs: "calc(16px + var(--safe-bottom))", md: 3 },
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {status === "no_access" ? <AccessDenied /> : <Outlet />}
        </Box>
      </Box>
    </Box>
  );
}

/* ───── sidebar component ────────────────────────────────────────────── */

function Sidebar({
  sections,
  expanded,
  width,
  onToggle,
  user,
  role,
  mobile,
  onItemClick,
  onSignOut,
}: {
  sections: NavSection[];
  expanded: boolean;
  width: number;
  onToggle: () => void;
  user: { email?: null | string } | null;
  role: Role | null;
  mobile: boolean;
  onItemClick?: () => void;
  onSignOut: () => void;
}) {
  const location = useLocation();
  const { totalSignals } = useRisksSummary();

  const handleLogoRefresh = async () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.clear();
    } catch {}

    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {}

    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((reg) => reg.unregister()));
      }
    } catch {}

    window.location.reload();
  };

  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: mobile ? "100%" : "100dvh",
        height: mobile ? "100%" : "100dvh",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        transition: "width 200ms ease",
        overflow: "hidden",
        ...(mobile
          ? {}
          : {
              position: "sticky",
              top: 0,
            }),
        ...(mobile
          ? {
              pt: "var(--safe-top)",
              pb: "var(--safe-bottom)",
            }
          : {}),
      }}
    >
      {/* ── logo ── */}
      <Box
        sx={{
          height: mobile ? 64 : 56,
          px: expanded ? 2 : 0,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <MuiLink
          component="button"
          type="button"
          onClick={handleLogoRefresh}
          underline="none"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            color: "text.primary",
            bgcolor: "transparent",
            border: 0,
            p: 0,
            font: "inherit",
            cursor: "pointer",
            "&:hover": { opacity: 0.75 },
          }}
        >
          <BrewisMark size={28} />
          {expanded && (
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              Brewis
            </Typography>
          )}
        </MuiLink>
      </Box>

      <Divider />

      {/* ── nav items ── */}
      <Box sx={{ flex: 1, py: 1, overflowY: "auto", overflowX: "hidden" }}>
        {sections.map((section, idx) => (
          <Box key={section.key}>
            {/* section label (expanded only) */}
            {expanded && (
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  px: 2,
                  pt: idx > 0 ? 2 : 1,
                  pb: 0.5,
                  fontSize: "0.625rem",
                  letterSpacing: 1,
                  color: "text.disabled",
                }}
              >
                {section.label}
              </Typography>
            )}
            {!expanded && idx > 0 && <Divider sx={{ my: 0.5, mx: 1 }} />}

            {section.tabs.map((tab) => {
              const active = location.pathname.startsWith(tab.to);
              const hasBadge = tab.to === "/risks" && totalSignals > 0;

              return (
                <SidebarItem
                  key={tab.to}
                  tab={tab}
                  active={active}
                  expanded={expanded}
                  onClick={onItemClick}
                  badge={hasBadge ? totalSignals : undefined}
                />
              );
            })}
          </Box>
        ))}
      </Box>

      <Divider />

      {/* ── footer: user + collapse toggle ── */}
      <Box sx={{ py: 1 }}>
        {/* user info */}
        <Box
          sx={{
            px: expanded ? 2 : 0,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "flex-start" : "center",
            gap: 1.5,
          }}
        >
          {/* avatar */}
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {(user?.email?.[0] ?? "U").toUpperCase()}
          </Box>
          {expanded && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {user?.email?.split("@")[0] ?? "User"}
              </Typography>
              {role && (
                <Typography variant="caption" color="text.disabled" noWrap>
                  {ROLE_LABELS[role]}
                </Typography>
              )}
            </Box>
          )}
          {expanded && (
            <Tooltip title="Вийти">
              <IconButton size="small" onClick={onSignOut} sx={{ color: "text.secondary" }}>
                <LogOut size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* collapse / expand */}
        {!mobile && (
          <Box sx={{ display: "flex", justifyContent: expanded ? "flex-end" : "center", px: 1 }}>
            <Tooltip title={expanded ? "Згорнути" : "Розгорнути"}>
              <IconButton size="small" onClick={onToggle} sx={{ color: "text.secondary" }}>
                {expanded ? <ChevronsLeft size={18} /> : <ChevronsRight size={18} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ───── sidebar item ─────────────────────────────────────────────────── */

function SidebarItem({
  tab,
  active,
  expanded,
  onClick,
  badge,
}: {
  tab: NavTab;
  active: boolean;
  expanded: boolean;
  onClick?: () => void;
  badge?: number;
}) {
  const theme = useTheme();
  const Icon = tab.icon;

  const item = (
    <Box
      component={Link}
      to={tab.to}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: expanded ? 2 : 0,
        py: 0.9,
        mx: expanded ? 1 : 0.75,
        borderRadius: 1,
        textDecoration: "none",
        color: active ? "primary.main" : "text.secondary",
        bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
        fontWeight: active ? 600 : 400,
        fontSize: "0.8125rem",
        justifyContent: expanded ? "flex-start" : "center",
        position: "relative",
        minHeight: 44,
        transition: "background 150ms, color 150ms",
        "&:hover": {
          bgcolor: active
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.text.primary, 0.04),
        },
      }}
    >
      <Box sx={{ position: "relative", display: "flex" }}>
        <Icon size={18} />
        {!expanded && badge != null && (
          <Box
            sx={{
              position: "absolute",
              top: -3,
              right: -5,
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "error.main",
            }}
          />
        )}
      </Box>
      {expanded && (
        <>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              fontWeight: "inherit",
              color: "inherit",
              fontSize: "inherit",
            }}
          >
            {tab.label}
          </Typography>
          {badge != null && (
            <Box
              sx={{
                minWidth: 18,
                height: 18,
                borderRadius: 9,
                bgcolor: "error.main",
                color: "error.contrastText",
                fontSize: "0.6875rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
              }}
            >
              {badge}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  if (!expanded) {
    return (
      <Tooltip title={tab.label} placement="right" arrow>
        {item}
      </Tooltip>
    );
  }

  return item;
}

/* ───── sub-tabs bar (horizontal, inside main area) ──────────────────── */

function SubTabsBar({ section }: { section: NavSection }) {
  const location = useLocation();
  const { totalSignals } = useRisksSummary();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        px: { xs: 2, md: 3 },
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        overflowX: "auto",
        whiteSpace: "nowrap",
      }}
    >
      {section.tabs.map((tab) => {
        const active = location.pathname.startsWith(tab.to);
        const hasBadge = tab.to === "/risks" && totalSignals > 0;

        return (
          <Button
            key={tab.to}
            component={Link}
            to={tab.to}
            size="small"
            variant={active ? "contained" : "text"}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: active ? 600 : 400,
              fontSize: "0.8125rem",
              minWidth: 0,
              px: 2,
              borderRadius: 1,
              ...(active
                ? {}
                : {
                    color: "text.secondary",
                    "&:hover": { bgcolor: "action.hover" },
                  }),
            }}
          >
            {tab.label}
            {hasBadge && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  bgcolor: active ? "common.white" : "error.main",
                  color: active ? "primary.main" : "error.contrastText",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 0.5,
                }}
              >
                {totalSignals}
              </Box>
            )}
          </Button>
        );
      })}
    </Box>
  );
}

/* ───── access denied ────────────────────────────────────────────────── */

function AccessDenied() {
  return (
    <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
      <Card sx={{ maxWidth: 440 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h3">Немає доступу</Typography>
            <Alert severity="info" sx={{ bgcolor: "transparent", px: 0 }}>
              Ти залогінений, але не маєш запису у колекції <code>admins/</code> — значить, доступ
              до бек-офісу для тебе ще не налаштували.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Звернись до власника (#ops у Slack) з проханням додати твій UID і роль. Після цього
              перезавантаж сторінку.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
